import { useState } from "react";
import {
  BookOpen,
  Video,
  FileText,
  Code,
  Users,
  MessageCircle,
  ArrowRight,
  Search,
  Star,
  Clock,
  User,
  Lightbulb,
  Shield,
  Zap,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Interface for resource item properties
 */
interface ResourceItem {
  id: string;
  title: string;
  description: string;
  category: string;
  type:
    | "documentation"
    | "tutorial"
    | "guide"
    | "video"
    | "template"
    | "api"
    | "blog";
  difficulty: "beginner" | "intermediate" | "advanced";
  readTime: string;
  author: string;
  date: string;
  featured: boolean;
  tags: string[];
  link: string;
}

/**
 * Interface for resource category properties
 */
interface ResourceCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  count: number;
}

/**
 * Main resources page component
 */
export function ResourcesPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  /**
   * Sample resource data for AI store resources
   */
  const resources: ResourceItem[] = [
    {
      id: "1",
      title: "Getting Started with AI Employees",
      description:
        "Complete guide to setting up and deploying your first AI employee in the marketplace.",
      category: "Getting Started",
      type: "guide",
      difficulty: "beginner",
      readTime: "10 min",
      author: "Taqat Team",
      date: "2024-03-15",
      featured: true,
      tags: ["setup", "deployment", "basics"],
      link: "/docs/getting-started",
    },
    {
      id: "2",
      title: "AI Employee Builder API Reference",
      description:
        "Comprehensive API documentation for creating and managing AI employees programmatically.",
      category: "API Documentation",
      type: "api",
      difficulty: "advanced",
      readTime: "30 min",
      author: "Developer Team",
      date: "2024-03-12",
      featured: true,
      tags: ["api", "reference", "development"],
      link: "/docs/api-reference",
    },
    {
      id: "3",
      title: "Building Custom AI Workflows",
      description:
        "Learn how to create sophisticated workflows and automation with AI employees.",
      category: "Tutorials",
      type: "tutorial",
      difficulty: "intermediate",
      readTime: "25 min",
      author: "Sarah Johnson",
      date: "2024-03-10",
      featured: false,
      tags: ["workflows", "automation", "advanced"],
      link: "/docs/workflows",
    },
    {
      id: "4",
      title: "Best Practices for AI Employee Security",
      description:
        "Essential security considerations when deploying AI employees in production environments.",
      category: "Security",
      type: "guide",
      difficulty: "intermediate",
      readTime: "15 min",
      author: "Security Team",
      date: "2024-03-08",
      featured: false,
      tags: ["security", "best-practices", "production"],
      link: "/docs/security",
    },
    {
      id: "5",
      title: "AI Employee Templates Library",
      description:
        "Pre-built templates for common use cases: customer service, data analysis, content creation.",
      category: "Templates",
      type: "template",
      difficulty: "beginner",
      readTime: "5 min",
      author: "Template Team",
      date: "2024-03-05",
      featured: true,
      tags: ["templates", "examples", "quick-start"],
      link: "/templates",
    },
    {
      id: "6",
      title: "Video: AI Employee Marketplace Overview",
      description:
        "Watch this 15-minute overview of the AI employee marketplace and its key features.",
      category: "Video Tutorials",
      type: "video",
      difficulty: "beginner",
      readTime: "15 min",
      author: "Video Team",
      date: "2024-03-01",
      featured: false,
      tags: ["video", "overview", "introduction"],
      link: "/videos/marketplace-overview",
    },
    {
      id: "7",
      title: "Performance Optimization Guide",
      description:
        "Optimize your AI employees for better performance, cost efficiency, and response times.",
      category: "Optimization",
      type: "guide",
      difficulty: "advanced",
      readTime: "20 min",
      author: "Performance Team",
      date: "2024-02-28",
      featured: false,
      tags: ["performance", "optimization", "cost"],
      link: "/docs/optimization",
    },
    {
      id: "8",
      title: "Case Study: Enterprise AI Implementation",
      description:
        "Real-world case study of how a Fortune 500 company implemented AI employees at scale.",
      category: "Case Studies",
      type: "blog",
      difficulty: "intermediate",
      readTime: "12 min",
      author: "Case Study Team",
      date: "2024-02-25",
      featured: true,
      tags: ["case-study", "enterprise", "success-story"],
      link: "/blog/enterprise-case-study",
    },
  ];

  /**
   * Resource categories with metadata
   */
  const categories: ResourceCategory[] = [
    {
      id: "getting-started",
      name: "Getting Started",
      description: "Essential resources for new users",
      icon: <Lightbulb className="w-5 h-5" />,
      count: 3,
    },
    {
      id: "api-docs",
      name: "API Documentation",
      description: "Developer resources and API references",
      icon: <Code className="w-5 h-5" />,
      count: 5,
    },
    {
      id: "tutorials",
      name: "Tutorials",
      description: "Step-by-step learning guides",
      icon: <BookOpen className="w-5 h-5" />,
      count: 8,
    },
    {
      id: "security",
      name: "Security",
      description: "Security best practices and guidelines",
      icon: <Shield className="w-5 h-5" />,
      count: 4,
    },
    {
      id: "templates",
      name: "Templates",
      description: "Pre-built AI employee templates",
      icon: <FileText className="w-5 h-5" />,
      count: 12,
    },
    {
      id: "videos",
      name: "Video Content",
      description: "Video tutorials and demos",
      icon: <Video className="w-5 h-5" />,
      count: 6,
    },
  ];

  /**
   * Filter resources based on search term and filters
   */
  const filteredResources = resources.filter((resource) => {
    const matchesSearch =
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory =
      categoryFilter === "all" ||
      resource.category.toLowerCase().includes(categoryFilter.toLowerCase());
    const matchesType = typeFilter === "all" || resource.type === typeFilter;

    return matchesSearch && matchesCategory && matchesType;
  });

  /**
   * Get featured resources
   */
  const featuredResources = resources.filter((resource) => resource.featured);

  /**
   * Get icon for resource type
   */
  const getTypeIcon = (type: ResourceItem["type"]) => {
    switch (type) {
      case "documentation":
      case "guide":
        return <BookOpen className="w-4 h-4" />;
      case "tutorial":
        return <Target className="w-4 h-4" />;
      case "video":
        return <Video className="w-4 h-4" />;
      case "template":
        return <FileText className="w-4 h-4" />;
      case "api":
        return <Code className="w-4 h-4" />;
      case "blog":
        return <MessageCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  /**
   * Get badge variant for difficulty level
   */
  const getDifficultyVariant = (
    difficulty: ResourceItem["difficulty"]
  ): "default" | "secondary" | "destructive" => {
    switch (difficulty) {
      case "beginner":
        return "default";
      case "intermediate":
        return "secondary";
      case "advanced":
        return "destructive";
      default:
        return "default";
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Knowledge Center
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-neutral-900 mb-6">
              AI Employee Store
              <span className="text-primary block">Resources</span>
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto mb-8">
              Everything you need to build, deploy, and manage AI employees.
              From getting started guides to advanced API documentation and
              real-world case studies.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search resources, guides, and documentation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-14 text-lg border-neutral-200 focus:border-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <Card
                  key={category.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer border-neutral-200/60"
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                        {category.icon}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          {category.name}
                        </CardTitle>
                        <CardDescription>
                          {category.description}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {category.count}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <Tabs defaultValue="all" className="w-full">
              <div className="flex flex-col lg:flex-row gap-8 mb-8">
                <div className="flex-1">
                  <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:flex">
                    <TabsTrigger value="all">All Resources</TabsTrigger>
                    <TabsTrigger value="featured">Featured</TabsTrigger>
                    <TabsTrigger value="popular">Popular</TabsTrigger>
                    <TabsTrigger value="recent">Recent</TabsTrigger>
                  </TabsList>
                </div>

                {/* Filters */}
                <div className="flex gap-4">
                  <Select
                    value={categoryFilter}
                    onValueChange={setCategoryFilter}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="guide">Guides</SelectItem>
                      <SelectItem value="tutorial">Tutorials</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="template">Templates</SelectItem>
                      <SelectItem value="api">API Docs</SelectItem>
                      <SelectItem value="blog">Blog Posts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <TabsContent value="all" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredResources.map((resource) => (
                    <Card
                      key={resource.id}
                      className="hover:shadow-lg transition-all duration-200 group border-neutral-200/60"
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getTypeIcon(resource.type)}
                              <Badge variant="outline" className="text-xs">
                                {resource.category}
                              </Badge>
                              {resource.featured && (
                                <Badge className="text-xs bg-primary">
                                  <Star className="w-3 h-3 mr-1" />
                                  Featured
                                </Badge>
                              )}
                            </div>
                            <CardTitle className="group-hover:text-primary transition-colors">
                              {resource.title}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              {resource.description}
                            </CardDescription>
                          </div>
                          <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-neutral-500">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {resource.readTime}
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {resource.author}
                            </div>
                          </div>
                          <Badge
                            variant={getDifficultyVariant(resource.difficulty)}
                            className="text-xs"
                          >
                            {resource.difficulty}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-3">
                          {resource.tags.slice(0, 3).map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {filteredResources.length === 0 && (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">
                      No resources found
                    </h3>
                    <p className="text-neutral-600">
                      Try adjusting your search or filters
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="featured" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {featuredResources.map((resource) => (
                    <Card
                      key={resource.id}
                      className="hover:shadow-lg transition-all duration-200 group border-neutral-200/60"
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getTypeIcon(resource.type)}
                              <Badge variant="outline" className="text-xs">
                                {resource.category}
                              </Badge>
                              <Badge className="text-xs bg-primary">
                                <Star className="w-3 h-3 mr-1" />
                                Featured
                              </Badge>
                            </div>
                            <CardTitle className="group-hover:text-primary transition-colors">
                              {resource.title}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              {resource.description}
                            </CardDescription>
                          </div>
                          <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-neutral-500">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {resource.readTime}
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {resource.author}
                            </div>
                          </div>
                          <Badge
                            variant={getDifficultyVariant(resource.difficulty)}
                            className="text-xs"
                          >
                            {resource.difficulty}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-3">
                          {resource.tags.slice(0, 3).map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="popular" className="space-y-6">
                <div className="text-center py-12">
                  <Zap className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-neutral-900 mb-2">
                    Popular Content Coming Soon
                  </h3>
                  <p className="text-neutral-600">
                    We're tracking popular resources and will display them here
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="recent" className="space-y-6">
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-neutral-900 mb-2">
                    Recent Updates Coming Soon
                  </h3>
                  <p className="text-neutral-600">
                    Check back soon for the latest resources and updates
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-primary/5 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Need Help Getting Started?
            </h2>
            <p className="text-lg text-neutral-600 mb-8">
              Our team is here to help you succeed with AI employees. Get
              personalized guidance and support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary-600">
                <MessageCircle className="w-5 h-5 mr-2" />
                Contact Support
              </Button>
              <Button size="lg" variant="outline">
                <Users className="w-5 h-5 mr-2" />
                Join Community
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
